import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import RefreshIcon from "@mui/icons-material/Refresh";
import { canSeeSmsProviderPricing } from "../../utils/userRoles";
import { acme } from "../../theme/acmeTheme";

function formatCredits(value) {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "—";
  return Math.max(0, Math.floor(n)).toLocaleString();
}

function formatMoney(value) {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(n);
  } catch {
    return `€${n.toFixed(2)}`;
  }
}

function statusColor(status) {
  if (status === "healthy") return "success";
  if (status === "warning") return "warning";
  if (status === "critical" || status === "depleted") return "error";
  return "default";
}

function statusLabel(status) {
  switch (status) {
    case "healthy":
      return "Healthy";
    case "warning":
      return "Low coverage";
    case "critical":
      return "Critical";
    case "depleted":
      return "Depleted";
    default:
      return "Unknown";
  }
}

/**
 * Staff-only panel: BulkGate wallet vs total user smsCredits liability.
 */
export default function SmsProviderBalancePanel({
  user,
  variant = "paper",
  pollMs = 60000,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const show = user && canSeeSmsProviderPricing(user.role);

  const load = useCallback(async (force = false) => {
    if (!show) return;
    setLoading(true);
    setError(null);
    try {
      const url = force
        ? "/api/sms-billing/provider-status/refresh"
        : "/api/sms-billing/provider-status";
      const method = force ? "post" : "get";
      const { data: payload } = await axios({ method, url, withCredentials: true });
      setData(payload);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Could not load BulkGate balance.",
      );
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    load(false);
    const id = setInterval(() => load(false), pollMs);
    return () => clearInterval(id);
  }, [show, load, pollMs]);

  if (!show) return null;

  const coverageValue =
    data?.coveragePercent != null
      ? Math.min(100, Math.max(0, data.coveragePercent))
      : data?.userLiability === 0 && data?.bulkgateBalance != null
        ? 100
        : 0;

  const content = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalanceWalletIcon sx={{ color: acme.colors.brand }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            BulkGate SMS float
          </Typography>
          {data?.status && (
            <Chip
              size="small"
              color={statusColor(data.status)}
              label={statusLabel(data.status)}
            />
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => load(true)}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data?.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          BulkGate poll error: {data.error}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            BulkGate balance
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#2c3e50" }}>
            {formatCredits(data?.bulkgateBalance)} cr
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data?.providerCostEur != null
              ? `≈ ${formatMoney(data.providerCostEur)} our cost`
              : "—"}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            User liability (Σ smsCredits)
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#2c3e50" }}>
            {formatCredits(data?.userLiability)} cr
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data?.liabilityCostEur != null
              ? `≈ ${formatMoney(data.liabilityCostEur)} if fully spent`
              : "—"}
          </Typography>
        </Paper>
      </Box>

      <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">
          Coverage
          {data?.coveragePercent != null
            ? `: ${data.coveragePercent}%`
            : data?.userLiability === 0
              ? ": no user liability"
              : ""}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Target: {formatCredits(data?.targetBalanceCredits)} cr
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={coverageValue}
        sx={{
          height: 10,
          borderRadius: 1,
          mb: 2,
          "& .MuiLinearProgress-bar": {
            backgroundColor:
              data?.status === "healthy"
                ? "#4caf50"
                : data?.status === "warning"
                  ? "#ff9800"
                  : "#d32f2f",
          },
        }}
      />

      {data?.recommendedTopUpCredits > 0 && (
        <Alert severity={data.status === "healthy" ? "info" : "warning"}>
          Recommended BulkGate top-up:{" "}
          <strong>{formatCredits(data.recommendedTopUpCredits)} credits</strong>{" "}
          (≈ {formatMoney(data.recommendedTopUpEur)}). Buy credits in the BulkGate
          portal, then click Refresh.
        </Alert>
      )}

      {!data?.canSend && (
        <Alert severity="error" sx={{ mt: 2 }}>
          New SMS campaigns are blocked until BulkGate balance covers user
          liability and the safety buffer.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Wallet: {data?.wallet || "—"}
        {data?.at
          ? ` • Updated ${new Date(data.at).toLocaleString()}`
          : ""}
      </Typography>
    </>
  );

  if (variant === "inline") {
    return <Box sx={{ mb: 3 }}>{content}</Box>;
  }

  return (
    <Paper sx={{ p: 3, mb: 3, boxShadow: 3, border: "1px solid #e2e8f0" }}>
      {content}
    </Paper>
  );
}
